// MyOrdersSidebar.jsx
// Shows user's own orders (pending/approved/rejected) using localStorage IDs
// Orders are fetched live from Firebase and persist via localStorage

import React, { useState, useEffect } from "react";
import {
  X, ClipboardList, Clock, CheckCircle, XCircle,
  Package, Phone, CreditCard, Key, Copy,
  Loader, ShieldCheck, AlertCircle
} from "lucide-react";
import { ref as dbRef, onValue } from "firebase/database";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../i18n/LanguageContext";
import "./MyOrdersSidebar.css";

const formatDate = (ts, lang) => {
  if (!ts) return "";
  const d = new Date(ts);
  const locale = lang === "ar" ? "ar-EG" : "en-US";
  return d.toLocaleString(locale, {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ─── Single Order Card ─────────────────────────────────────────────────────
const OrderCard = ({ order }) => {
  const [copied, setCopied] = useState(false);
  const { t, lang } = useLanguage();

  const handleCopy = () => {
    if (!order.generatedPassword) return;
    navigator.clipboard.writeText(order.generatedPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusConfig = {
    pending:  { label: t("orders.pendingStatusLabel"),  icon: <Clock size={12} /> },
    approved: { label: t("orders.approvedStatusLabel"), icon: <CheckCircle size={12} /> },
    rejected: { label: t("orders.rejectedStatusLabel"), icon: <XCircle size={12} /> },
  };
  const s = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className={`mos-order-card mos-order-${order.status}`}>
      {/* Top Row */}
      <div className="mos-card-top">
        <div className="mos-card-pkg-info">
          <span className="mos-card-pkg-name">
            {order.packageName || t("orders.packageUnspecified")}
          </span>
          {order.packagePrice && (
            <span className="mos-card-pkg-price">{order.packagePrice}</span>
          )}
        </div>
        <div className={`mos-card-status ${order.status}`}>
          {s.icon}
          <span>{s.label}</span>
        </div>
      </div>

      {/* Meta Info */}
      <div className="mos-card-meta">
        <div className="mos-meta-item">
          <CreditCard size={12} />
          <span className="mos-meta-value">{order.paymentMethod}</span>
        </div>
        <div className="mos-meta-item">
          <Phone size={12} />
          <span className="mos-meta-value" dir="ltr">{order.phone}</span>
        </div>
        {order.createdAt && (
          <div className="mos-meta-item">
            <Clock size={12} />
            <span className="mos-meta-value">{formatDate(order.createdAt, lang)}</span>
          </div>
        )}
      </div>

      {/* Pending indicator */}
      {order.status === "pending" && (
        <div className="mos-meta-item" style={{ color: "#ffbb00", fontSize: 12 }}>
          <Clock size={12} />
          <span>{t("orders.reviewingProof")}</span>
        </div>
      )}

      {/* Password (approved) */}
      {order.status === "approved" && order.generatedPassword && (
        <div className="mos-password-box">
          <div className="mos-password-label">
            <Key size={13} />
            <span>{t("orders.packagePasswordLabel")}</span>
          </div>
          <code className="mos-password-code">{order.generatedPassword}</code>
          <button className="mos-password-copy-btn" onClick={handleCopy}>
            {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
            <span>{copied ? t("orders.passwordCopied") : t("orders.copyPasswordBtn")}</span>
          </button>
        </div>
      )}

      {/* Rejected */}
      {order.status === "rejected" && (
        <div className="mos-rejected-note">
          <AlertCircle size={13} />
          <span>{t("orders.rejectedProofNote")}</span>
        </div>
      )}
    </div>
  );
};

// ─── Main MyOrdersSidebar ──────────────────────────────────────────────────
const MyOrdersSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Load proof IDs from localStorage
  const [myProofIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("myProofIds") || "[]");
    } catch { return []; }
  });

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // Subscribe live to Firebase for our proof IDs
  useEffect(() => {
    if (!isOpen) return;
    if (myProofIds.length === 0) {
      setLoading(false);
      setOrders([]);
      return;
    }

    const ref = dbRef(db, "pendingPayments");
    const unsub = onValue(ref, (snap) => {
      setLoading(false);
      const data = snap.val();
      if (!data) { setOrders([]); return; }
      const list = myProofIds
        .map((id) => data[id] ? { ...data[id], id } : null)
        .filter(Boolean)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setOrders(list);
    });

    return () => unsub();
  }, [isOpen, myProofIds]);

  const pendingCount = orders.filter((o) => o.status === "pending").length;
  const approvedCount = orders.filter((o) => o.status === "approved").length;
  const rejectedCount = orders.filter((o) => o.status === "rejected").length;

  const filtered = filterStatus === "all"
    ? orders
    : orders.filter((o) => o.status === filterStatus);

  const tabs = [
    { key: "all", label: t("home.filterAll"), count: orders.length },
    { key: "pending", label: t("orders.pending"), count: pendingCount },
    { key: "approved", label: t("orders.approved"), count: approvedCount },
    { key: "rejected", label: t("orders.rejected"), count: rejectedCount },
  ];

  return (
    <>
      <div
        className={`my-orders-sidebar-overlay ${isOpen ? "active" : ""}`}
        onClick={onClose}
      />
      <div className={`my-orders-sidebar ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="mos-header">
          <div className="mos-header-title">
            <ClipboardList size={19} />
            <span>{t("orders.title")}</span>
            {orders.length > 0 && (
              <span className="mos-header-badge">{orders.length}</span>
            )}
          </div>
          <button className="mos-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Stats Bar */}
        {orders.length > 0 && (
          <div className="mos-stats-bar">
            <div className="mos-stat mos-stat-pending">
              <span className="mos-stat-num">{pendingCount}</span>
              <span className="mos-stat-label">{t("orders.pending")}</span>
            </div>
            <div className="mos-stat mos-stat-approved">
              <span className="mos-stat-num">{approvedCount}</span>
              <span className="mos-stat-label">{t("orders.approved")}</span>
            </div>
            <div className="mos-stat mos-stat-rejected">
              <span className="mos-stat-num">{rejectedCount}</span>
              <span className="mos-stat-label">{t("orders.rejected")}</span>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        {orders.length > 0 && (
          <div className="mos-filter-tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`mos-filter-tab ${filterStatus === tab.key ? `active-${tab.key}` : ""}`}
                onClick={() => setFilterStatus(tab.key)}
              >
                <span>{tab.label}</span>
                <span className="mos-tab-count">{tab.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="mos-content">
          {loading ? (
            <div className="mos-loading">
              <Loader size={28} color="#ffbb00" className="mos-spin" />
              <span>{t("orders.loadingOrders")}</span>
            </div>
          ) : myProofIds.length === 0 || orders.length === 0 ? (
            <div className="mos-empty">
              <div className="mos-empty-icon">📋</div>
              <h3 className="mos-empty-title">{t("orders.noOrders")}</h3>
              <p className="mos-empty-desc">
                {t("orders.emptyOrdersDesc")}
              </p>
              <button
                className="mos-empty-action"
                onClick={() => { onClose(); navigate("/payment"); }}
              >
                <Package size={15} style={{ marginLeft: 6, verticalAlign: "middle" }} />
                {t("orders.goToPayment")}
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="mos-empty">
              <div className="mos-empty-icon">🔍</div>
              <p className="mos-empty-desc">{t("orders.emptyFilterDesc")}</p>
            </div>
          ) : (
            filtered.map((order) => (
              <OrderCard key={order.id} order={order} />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mos-footer">
          <button
            className="mos-go-payment-btn"
            onClick={() => { onClose(); navigate("/payment"); }}
          >
            <CreditCard size={16} />
            <span>{t("orders.submitNewProof")}</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default MyOrdersSidebar;
